import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, PhoneIcon, MailIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { NavDropdown } from './navigation/NavDropdown';
import { NavIconGlyph } from './navigation/NavIconGlyph';
import { navItems } from '../data/navigation';
import type { Route } from '../hooks/useHashRoute';
type NavbarProps = {
  route: Route;
};
export function Navbar({
  route
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const closeMobile = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileGroup(null);
  };
  return <header className={`sticky top-0 z-40 w-full bg-white transition-[padding,box-shadow] duration-200 ease-out ${isScrolled ? 'shadow-md py-2' : 'py-4'}`} data-id="element-503" data-mp="9nfyonhmzevj">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-504" data-mp="jmx20y9f1goc">
        <div className="flex justify-between items-center gap-6" data-id="element-505" data-mp="jkklmpjb3ede">
          <a href="#/" aria-label="AdROI home" className="flex flex-shrink-0 items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-4" data-id="element-506" data-mp="okp0yc8ha321">
            <Logo size="sm" data-id="element-507" data-mp="3s04917xaq2kj" />
          </a>

          <nav aria-label="Main" className="hidden lg:flex items-center gap-7" data-id="element-508" data-mp="v4j5wo29buc">
            {navItems.map(item => item.children ? <NavDropdown key={item.name} label={item.name} items={item.children} data-id="element-509" data-mp="5opbb82z3blm" /> : <a key={item.name} href={item.href} aria-current={item.route && item.route === route ? 'page' : undefined} className={`rounded-sm text-sm font-medium transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${item.route && item.route === route ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-510" data-mp="39bzb4h3blbtn">
                  {item.name}
                </a>)}
          </nav>

          <div className="hidden lg:flex items-center gap-5" data-id="element-511" data-mp="2ddc8pij7hlbq">
            <a href="tel:17022134801" className="hidden xl:flex items-center whitespace-nowrap text-sm font-medium text-gray-600 transition-colors duration-150 ease-out hover:text-brand-red" data-id="element-512" data-mp="2devprbcos0ek">
              <PhoneIcon className="h-4 w-4 mr-2" aria-hidden="true" data-id="element-513" data-mp="7kci3omtejgs" />
              (702) 213-4801
            </a>
            <a href="#/contact" aria-current={route === 'contact' ? 'page' : undefined} className="whitespace-nowrap rounded-md bg-brand-red px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-514" data-mp="2ljd6kllg0s8">
              CONTACT US
            </a>
          </div>

          <button onClick={() => setIsMobileMenuOpen(v => !v)} className="lg:hidden text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red rounded-sm" aria-label="Toggle menu" aria-expanded={isMobileMenuOpen} data-id="element-515" data-mp="57s9hiyu1wgo">
            {isMobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-516" data-mp="o3s1uuvq320s" /> : <MenuIcon className="h-6 w-6" data-id="element-517" data-mp="o30gsdzr4mor" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-518" data-mp="1btcllfzpa3b4">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-519" data-mp="1gkm9oc5loykw">
            <div className="max-h-[75vh] overflow-y-auto px-4 pt-2 pb-6 shadow-lg" data-id="element-520" data-mp="3f4snwx18kgp5">
              {navItems.map(item => item.children ? <div key={item.name} data-id="element-521" data-mp="bn0zfsmb6evp">
                    <button type="button" onClick={() => setOpenMobileGroup(g => g === item.name ? null : item.name)} aria-expanded={openMobileGroup === item.name} className="flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50" data-id="element-522" data-mp="blpmp3avxwsg">
                      {item.name}
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ease-out ${openMobileGroup === item.name ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-523" data-mp="ufd2yiop7m7f" />
                    </button>
                    {openMobileGroup === item.name && <ul className="mb-2 ml-3 border-l border-gray-200 pl-2" data-id="element-524" data-mp="2h96uxw97aleq">
                        {item.children.map(child => <li key={child.name} data-id="element-525" data-mp="2mame1irtihuh">
                            <a href={child.href} onClick={closeMobile} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-brand-tint hover:text-brand-red" data-id="element-526" data-mp="2r8xnvti44scp">
                              <NavIconGlyph icon={child.icon} className="h-4 w-4 text-brand-red" data-id="element-527" data-mp="34yj3qv0m8rgi" />
                              {child.name}
                            </a>
                          </li>)}
                      </ul>}
                  </div> : <a key={item.name} href={item.href} onClick={closeMobile} className="block rounded-md px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-brand-red" data-id="element-528" data-mp="1ew0pfcdz99z8">
                    {item.name}
                  </a>)}
              <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col space-y-4" data-id="element-529" data-mp="1xkxft5697ic6">
                <a href="tel:17022134801" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-530" data-mp="1tjlqz1v7f151">
                  <PhoneIcon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" data-id="element-531" data-mp="27jwgo1ks29om" />
                  (702) 213-4801
                </a>
                <a href="mailto:Results@GetAdROI.com" className="flex items-center px-3 text-base font-medium text-gray-600" data-id="element-532" data-mp="d6doz8dfhlb8">
                  <MailIcon className="h-5 w-5 mr-3 text-gray-400" aria-hidden="true" data-id="element-533" data-mp="883s8tc22cni" />
                  Results@GetAdROI.com
                </a>
                <a href="#/contact" onClick={closeMobile} className="w-full rounded-md bg-brand-red px-4 py-3 text-center text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-red-dark" data-id="element-534" data-mp="2jwqrjwu35khm">
                  CONTACT US
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}